import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { SymbolType, CdnObjectType } from '@/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { CdnMedia } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Spell } from '../components/Spell';
import { Activity, ActivityStep, ActivityStepProps } from '../Activity';
import { ActivityTypeProps } from '../types';
import { getStepNumber } from '../utils';

interface ReadingReadSentencesStepProps extends ActivityStepProps {
  index: number;
  sentence: string;
}

const ReadingReadSentencesStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  stepNumber,
  totalSteps,
  index,
  sentence,
}: ReadingReadSentencesStepProps) => {
  const { enablePlayButton, disablePlayButton } = useLessonPlayer();

  const spellRef = useRef<Spell>(null);

  const [imageShowing, setImageShowing] = useState<boolean>(false);

  const [playCount, setPlayCount] = useState(0);

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={`Say the sounds and blend them to make words and sentences.`}
      showTitle={true}
      showActivityPagination={false}
      onSlide={() => {
        enablePlayButton({
          onClick: () => {
            spellRef.current?.play();
          },
          hint: playCount < 2,
        });
      }}
      onBeforeLeave={() => {
        disablePlayButton();

        return true;
      }}
    >
      <WhitePanel mode="basic" kind="fill" color="paper" gap={true}>
        <CdnMedia
          className={clsx(
            'block mx-auto w-auto h-0 bg-white rounded-xl',
            'opacity-0 transition-all duration-300 ease-in-out',
            imageShowing && 'h-64 opacity-100',
          )}
          symbol={sentence}
          symbolType={SymbolType.Sentence}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <Spell
          ref={spellRef}
          input={sentence}
          sounds={soundsData?.getPhonicsSounds.map((sound) => sound.name) || []}
          muteAudio={playCount < 1}
          onEnded={() =>
            setTimeout(() => {
              setPlayCount(playCount + 1);
              setImageShowing(playCount > 0);
            }, 750)
          }
        />
      </WhitePanel>
    </ActivityStep>
  );
};

export const ReadingReadSentences = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.sentences) {
    throw new Error('Sentences are required for this activity');
  }

  const { sentences } = activity;

  const STEPS_PER_SENTENCE = 1;
  const TOTAL_STEPS = sentences.length * STEPS_PER_SENTENCE + 1;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          parrotType="tablet-chair"
          total={sentences.length + 1}
        />,
        ...sentences.map((sentence, index) => (
          <ReadingReadSentencesStep
            key={`${activity.id}-${index}-1`}
            stepIdentifier={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            stepNumber={getStepNumber(index + 1, 0, STEPS_PER_SENTENCE)}
            totalSteps={TOTAL_STEPS}
            index={index + 1}
            sentence={sentence}
          />
        )),
      ]}
    />
  );
};
